import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/UI/Button';
import { ProjectCardProps } from '../types';

const ProjectCard = ({ project, onViewDetails }: ProjectCardProps) => {
  const handleViewDetails = () => {
    onViewDetails(project);
  };

  const statusColors = {
    completed: 'bg-[#0D9488]/10 text-[#0D9488] border-[#0D9488]/20',
    'in-progress': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    archived: 'bg-slate-500/10 text-slate-500 border-slate-500/20'
  };

  return (
    <div
      className="group bg-white border-2 border-[#0C2C39] rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-[12px_12px_0px_0px_#0C2C39] hover:-translate-x-1 hover:-translate-y-1"
      onClick={handleViewDetails}
    >
      {/* Image Container */}
      <div className="relative aspect-video overflow-hidden border-b-2 border-[#0C2C39]">
        <Image
          src={project.image}
          alt={project.alt}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C2C39]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {project.featured && (
          <div className="absolute top-4 left-4 bg-[#0D9488] text-white px-3 py-1 rounded-full text-[10px] font-black  tracking-widest flex items-center gap-2 shadow-lg">
            <Icon name="Star" size={12} className="fill-current" />
            Featured
          </div>
        )}

        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-[9px] font-black  tracking-widest border ${statusColors[project.status] || statusColors.archived} backdrop-blur-md`}>
            {project.status.replace('-', ' ')}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-[10px] font-black text-[#0D9488]  tracking-[0.3em]">
            {project.categories.join(' & ').replace('-', ' ')}
          </span>
          <div className="w-1.5 h-1.5 rounded-full bg-[#0C2C39]/10" />
          <span className="text-[10px] font-black text-[#0C2C39]/40  tracking-[0.3em]">
            {project.type}
          </span>
        </div>

        <h3 className="font-inter text-3xl font-black text-[#0C2C39] mb-4  tracking-tighter group-hover:text-[#0D9488] transition-colors leading-[0.9]">
          {project.title}
        </h3>

        <p className="text-sm text-[#0C2C39]/70 mb-8 leading-relaxed font-medium line-clamp-2">
          {project.shortDescription}
        </p>

        {/* Tech Stack - Centered */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {project.technologies.slice(0, 3).map((tech) => (
            <span
              key={tech}
              className="px-3 py-1 bg-white border-2 border-[#0C2C39]/10 text-[#0C2C39] text-[9px] font-black  rounded-lg group-hover:border-[#0D9488]/30 transition-colors"
            >
              {tech}
            </span>
          ))}
          {project.technologies.length > 3 && (
            <span className="px-2 py-1 text-[#0C2C39]/30 text-[9px] font-black  self-center">
              + {project.technologies.length - 3}
            </span>
          )}
        </div>

        <div className="pt-6 border-t border-[#0C2C39]/5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {project.githubUrl && (
              <button
                onClick={(e) => { e.stopPropagation(); window.open(project.githubUrl, '_blank'); }}
                className="text-[#0C2C39]/30 hover:text-[#0C2C39] transition-colors"
                title="Source Repository"
              >
                <Icon name="Github" size={20} />
              </button>
            )}
            {project.demoUrl && (
              <button
                onClick={(e) => { e.stopPropagation(); window.open(project.demoUrl, '_blank'); }}
                className="text-[#0C2C39]/30 hover:text-[#0D9488] transition-colors"
                title="Deployment Link"
              >
                <Icon name="ExternalLink" size={20} />
              </button>
            )}
          </div>

          <button className="flex items-center gap-2 text-[10px] font-black  tracking-widest text-[#0C2C39] hover:text-[#0D9488] transition-colors group/btn">
            Architecture Details
            <Icon name="ArrowRight" size={14} className="transition-transform group-hover/btn:translate-x-1.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;